import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  getSessionTimelineCache,
  setSessionTimelineCache,
} from "../lib/setterPerformanceCacheService";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";

// =============================================================================
// USE SETTER SESSION TIMELINE HOOK
// =============================================================================
// PURPOSE: Fetch individual session boundaries for the radial clock chart.
// Used by every SetterScorecard donut on mount + the Work Pattern modal.
//
// DATA SOURCE: get_setter_session_timeline RPC
//
// FEATURES:
// - Shared BATCHED fetch: all scorecards on the page join ONE in-flight RPC
//   (all setter IDs) per org+range instead of firing one call each. The old
//   per-setter split existed only to dodge the PGRST 1000-row cap, which
//   .range(0, 9999) already solves.
// - Cache-first with TTL (5 min live / 24h historical via cache service) —
//   a warm cache serves without re-firing the RPC. The old unconditional
//   stale-while-revalidate refetch made N setter cards = N RPCs per mount
//   even right after prefetch warmed the range.
// - Real cancellation: starting a batch for a NEW range aborts the previous
//   range's in-flight batch via .abortSignal() — the Postgres query stops,
//   not just the state write.
// =============================================================================

export interface SessionTimelineRow {
  setter_id: string;
  work_date: string; // ISO date string like "2026-02-08"
  session_start: string; // ISO timestamp
  session_end: string; // ISO timestamp
  message_count: number;
}

export interface UseSetterSessionTimelineOptions {
  organizationId: string | null;
  setterId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string; // org timezone for date boundary conversion (default "UTC")
}

// ─── Shared batch fetch (module-level — one in-flight batch per org) ─────────
interface PendingBatch {
  key: string;
  controller: AbortController;
  promise: Promise<Map<string, SessionTimelineRow[]>>;
}
const pendingBatches = new Map<string, PendingBatch>();

function parseRow(row: Record<string, unknown>): SessionTimelineRow {
  return {
    setter_id: row.setter_id as string,
    work_date: row.work_date as string,
    session_start: row.session_start as string,
    session_end: row.session_end as string,
    message_count: Number(row.message_count ?? 0),
  };
}

async function fetchTimelineBatch(
  orgId: string,
  setterIds: string[],
  dateRange: { start: Date; end: Date },
  timezone?: string
): Promise<Map<string, SessionTimelineRow[]>> {
  const key = `${dateRange.start.toISOString()}_${dateRange.end.toISOString()}_${setterIds.join(",")}`;
  const existing = pendingBatches.get(orgId);
  if (existing?.key === key) return existing.promise;
  // A batch for a different range is stale the moment a new range is requested —
  // abort it so the server stops working on an answer nobody will render.
  existing?.controller.abort();

  const controller = new AbortController();
  const promise = (async () => {
    const { data, error } = await supabase!
      .rpc("get_setter_session_timeline", {
        p_organization_id: orgId,
        p_setter_ids: setterIds,
        p_start_date: toOrgStartISO(dateRange.start, timezone),
        p_end_date: toOrgEndISO(dateRange.end, timezone),
      })
      .abortSignal(controller.signal)
      // Bypass PGRST's 1000-row cap — a month × N setters exceeds 1000 rows
      // (2,275 rows for 3 setters × 30 days) and would silently truncate.
      .range(0, 9999);
    if (error) throw error;

    const bySetter = new Map<string, SessionTimelineRow[]>();
    for (const sid of setterIds) bySetter.set(sid, []);
    for (const row of (data || []) as Record<string, unknown>[]) {
      const parsed = parseRow(row);
      const list = bySetter.get(parsed.setter_id);
      if (list) list.push(parsed);
      else bySetter.set(parsed.setter_id, [parsed]);
    }
    for (const [sid, list] of bySetter) {
      list.sort(
        (a, b) => new Date(a.session_start).getTime() - new Date(b.session_start).getTime()
      );
      setSessionTimelineCache(orgId, dateRange, sid, list);
    }
    return bySetter;
  })();

  const entry: PendingBatch = { key, controller, promise };
  pendingBatches.set(orgId, entry);
  promise
    .catch(() => {
      // Swallow here only to avoid an unhandled-rejection warning — every
      // consumer awaiting the promise still receives the rejection.
    })
    .finally(() => {
      if (pendingBatches.get(orgId) === entry) pendingBatches.delete(orgId);
    });
  return promise;
}

export function useSetterSessionTimeline(options: UseSetterSessionTimelineOptions) {
  const { organizationId, setterId, setterIds, dateRange, timezone } = options;

  // Initialize from cache synchronously — prevents clock chart spinner if data is pre-warmed
  const [data, setData] = useState<SessionTimelineRow[]>(
    () =>
      (organizationId && setterId
        ? getSessionTimelineCache<SessionTimelineRow[]>(organizationId, dateRange, setterId)
        : null) ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation token — a newer fetch (range change) supersedes an older one.
  // The older call's catch/finally must not clobber the newer call's state.
  const genRef = useRef(0);

  // Stable key so a new array identity with the same IDs doesn't churn the callback.
  const setterIdsKey = setterIds.join(",");
  const setterIdsRef = useRef(setterIds);
  setterIdsRef.current = setterIds;

  const fetch = useCallback(async () => {
    if (!organizationId || !setterId || !supabase) return;
    if (!dateRange.start || !dateRange.end) return;

    const myGen = ++genRef.current;

    // Cache-first: the cache service TTL-checks (5 min live / 24h historical).
    // Fresh hit = no RPC at all. Stale/missing = fall through to the batch.
    const cached = getSessionTimelineCache<SessionTimelineRow[]>(
      organizationId,
      dateRange,
      setterId
    );
    if (cached) {
      setData(cached);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const ids = setterIdsRef.current.length > 0 ? setterIdsRef.current : [setterId];
      const bySetter = await fetchTimelineBatch(
        organizationId,
        ids,
        { start: dateRange.start, end: dateRange.end },
        timezone
      );
      if (myGen !== genRef.current) return; // superseded by a newer fetch
      setData(bySetter.get(setterId) ?? []);
    } catch (err) {
      // Aborted = a newer range superseded this batch — the newer fetch owns the UI.
      if ((err as Error)?.name === "AbortError") return;
      if (myGen !== genRef.current) return;
      console.warn("Failed to fetch session timeline:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch session timeline");
    } finally {
      if (myGen === genRef.current) setIsLoading(false);
    }
  }, [organizationId, setterId, setterIdsKey, dateRange.start, dateRange.end, timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  // setterIds consumed via setterIdsRef; setterIdsKey stands in as the dep.

  useEffect(() => {
    return () => {
      // Invalidate in-flight state writes for this hook instance. The shared
      // batch is deliberately NOT aborted here — other scorecards may be
      // awaiting it; it aborts only when a new range supersedes it.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- genRef is a counter, not a DOM node; reading the latest value at cleanup is the point
      genRef.current++;
    };
  }, []);

  return { data, isLoading, error, fetch };
}
